<template>
  <div class="system-recommend page-shell">
    <div class="toolbar">
      <el-button
        type="primary"
        size="small"
        :loading="loading"
        icon="el-icon-refresh"
        @click="loadData"
      >
        重新计算
      </el-button>
      <el-select
        v-model="prosperityFilter"
        size="small"
        style="width: 160px"
        title="基于最新财报期的全市场景气度分位标签"
      >
        <el-option label="景气度：全部" value="all" />
        <el-option label="剔除任一负向" value="exclude-negative" />
        <el-option label="仅三项全正向" value="positive-only" />
      </el-select>
      <div class="theme-filter">
        <el-select
          v-model="themeFilterMode"
          size="small"
          class="theme-filter__mode"
          title="不含题材＝命中任一选中题材即剔除；仅含题材＝只保留命中题材的个股"
        >
          <el-option label="不含题材" value="exclude" />
          <el-option label="仅含题材" value="include" />
        </el-select>
        <el-select
          v-model="themeFilterThemes"
          size="small"
          class="theme-filter__value"
          multiple
          collapse-tags
          clearable
          :disabled="!themeOptions.length"
          :placeholder="themeOptions.length ? themeFilterPlaceholder : '暂无题材标签'"
        >
          <el-option-group
            v-for="g in themeOptionGroups"
            :key="g.category"
            :label="g.category"
          >
            <el-option
              v-for="o in g.options"
              :key="o.id"
              :label="`${o.name}（${o.count}）`"
              :value="o.id"
            />
          </el-option-group>
        </el-select>
      </div>
      <el-dropdown trigger="click" @command="handleExportCommand">
        <el-button type="success" size="small" icon="el-icon-download">
          导出<i class="el-icon-arrow-down el-icon--right" />
        </el-button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="all">导出全部（{{ filteredItems.length }} 只）</el-dropdown-item>
          <el-dropdown-item command="selected" :disabled="!selectedRows.length">
            导出已选（{{ selectedRows.length }} 只）
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
      <span class="toolbar-tip">
        基于 2026Q2 持仓 Top500 + P75 阈值（机构重仓 + 区间回调）
      </span>
    </div>

    <div v-if="summary" class="summary-bar">
      <div class="summary-grid">
        <div class="summary-cell">
          <span class="summary-label">数据季度</span>
          <span class="summary-value">{{ summary.quarter }}</span>
        </div>
        <div class="summary-cell">
          <span class="summary-label">基期</span>
          <span class="summary-value">{{ summary.asOf }}</span>
        </div>
        <div class="summary-cell">
          <span class="summary-label">最新日</span>
          <span class="summary-value">{{ summary.latestDate }}</span>
        </div>
        <div class="summary-cell">
          <span class="summary-label">Top500 池</span>
          <span class="summary-value">{{ summary.poolSize }} 只</span>
        </div>
        <div class="summary-cell">
          <span class="summary-label">可计算</span>
          <span class="summary-value">{{ summary.availableSize }} 只</span>
        </div>
        <div class="summary-cell summary-cell--accent">
          <span class="summary-label">推荐数量</span>
          <span class="summary-value">
            {{ filteredItems.length }} 只
            <em v-if="filterActive" class="summary-delta">/ 共 {{ summary.count }}</em>
          </span>
        </div>
      </div>
      <div class="threshold-bar">
        <span class="threshold-label">门槛：</span>
        <el-tag size="mini" type="info">市值 ≥ {{ summary.thresholds.marketValueP }} 亿</el-tag>
        <el-tag size="mini" type="info">家数 ≥ {{ summary.thresholds.sharesP }}</el-tag>
        <el-tag size="mini" type="danger">跌幅 &lt; 0</el-tag>
        <span class="threshold-label">权重：</span>
        <el-tag size="mini">跌幅 {{ Math.round(summary.thresholds.weights.decline * 100) }}%</el-tag>
        <el-tag size="mini">市值 {{ Math.round(summary.thresholds.weights.marketValue * 100) }}%</el-tag>
        <el-tag size="mini">家数 {{ Math.round(summary.thresholds.weights.shares * 100) }}%</el-tag>
        <span class="threshold-label">景气度：</span>
        <el-tag size="mini" type="info">{{ summary.prosperityPeriod || '无标签' }}</el-tag>
        <el-tag size="mini" type="info">全市场 P20/P80 三档</el-tag>
        <span class="threshold-label">题材：</span>
        <el-tag size="mini" type="info">
          {{ summary.themeTaggedStocks || 0 }} 只已打标 / {{ themeOptions.length }} 个题材
        </el-tag>
      </div>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      style="margin: 0 0 12px; text-align: left"
    />

    <div ref="tableShell" class="table-shell" v-loading="loading" element-loading-text="计算推荐中...">
      <el-table
        v-if="filteredItems.length"
        ref="recTable"
        :data="filteredItems"
        style="width: 100%"
        :height="tableHeight"
        :row-style="{ height: '34px', lineHeight: '34px' }"
        :cell-style="{ padding: '3px 0' }"
        stripe
        @selection-change="onSelectionChange"
      >
        <el-table-column type="selection" width="44" fixed="left" />
        <el-table-column prop="rank" label="排名" width="56" fixed="left" />
        <el-table-column prop="code" label="代码" width="84" fixed="left">
          <template slot-scope="{ row }">
            <el-link :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer">
              {{ row.code }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="名称" width="120" fixed="left">
          <template slot-scope="{ row }">
            <el-link :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer">
              {{ row.name }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column prop="industry" label="所属行业" width="100" show-overflow-tooltip />
        <el-table-column label="题材" width="168">
          <template slot-scope="{ row }">
            <div v-if="row.themes && row.themes.length" class="theme-tags">
              <span
                v-for="t in row.themes.slice(0, 2)"
                :key="t.themeId"
                class="theme-chip"
                :class="{ 'theme-chip--hit': themeFilterThemes.includes(t.themeId) }"
                :title="`点击排除题材「${t.themeName}」`"
                @click="toggleExcludeTheme(t.themeId)"
              >{{ t.themeName }}</span>
              <el-tooltip
                v-if="row.themes.length > 2"
                :content="themeTooltip(row)"
                placement="top"
              >
                <span class="theme-chip theme-chip--more">+{{ row.themes.length - 2 }}</span>
              </el-tooltip>
            </div>
            <span v-else class="theme-none">--</span>
          </template>
        </el-table-column>
        <el-table-column label="景气度" width="158" align="center">
          <template slot-scope="{ row }">
            <div v-if="row.prosperity" class="prosp-tags">
              <el-tooltip
                v-for="m in prosperityMeta"
                :key="m.key"
                :content="prospTooltip(row, m.key)"
                placement="top"
              >
                <span :class="prospTagClass(row, m.key)">{{ m.short }}</span>
              </el-tooltip>
            </div>
            <span v-else class="prosp-none">--</span>
          </template>
        </el-table-column>
        <el-table-column label="基期价" width="92" align="right">
          <template slot-scope="{ row }">{{ formatPrice(row.priceAsOf) }}</template>
        </el-table-column>
        <el-table-column label="最新价" width="92" align="right">
          <template slot-scope="{ row }">{{ formatPrice(row.priceLatest) }}</template>
        </el-table-column>
        <el-table-column label="涨跌额" width="92" align="right">
          <template slot-scope="{ row }">
            <span :class="row.change >= 0 ? 'up' : 'down'">
              {{ row.change >= 0 ? '+' : '' }}{{ formatPrice(row.change) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="declinePct" label="跌幅(%)" width="100" align="right" sortable>
          <template slot-scope="{ row }">
            <span class="down">{{ formatPct(row.declinePct) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="marketValue" label="市值(亿)" width="100" align="right" sortable />
        <el-table-column prop="shares" label="家数" width="80" align="right" sortable />
        <el-table-column prop="action" label="持股变化" width="84" align="center">
          <template slot-scope="{ row }">
            <el-tag
              :type="actionTagType(row.action)"
              size="mini"
              effect="plain"
            >{{ row.action || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="pd" label="跌幅百分位" width="116" align="right" sortable>
          <template slot-scope="{ row }">{{ formatPercentile(row.pd) }}</template>
        </el-table-column>
        <el-table-column prop="pm" label="市值百分位" width="116" align="right" sortable>
          <template slot-scope="{ row }">{{ formatPercentile(row.pm) }}</template>
        </el-table-column>
        <el-table-column prop="ps" label="家数百分位" width="116" align="right" sortable>
          <template slot-scope="{ row }">{{ formatPercentile(row.ps) }}</template>
        </el-table-column>
        <el-table-column prop="score" label="综合得分" width="100" align="right" fixed="right" sortable>
          <template slot-scope="{ row }">
            <span class="score-cell">{{ formatScore(row.score) }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div v-else-if="queried && !loading" class="empty-tip">
        当前没有满足 P75 阈值{{ filterActive ? '且通过景气度 / 题材筛选' : '' }}的推荐股票。
        可调整筛选条件或点击「重新计算」再试一次。
      </div>
    </div>
  </div>
</template>

<script>
import { fetchWithCredentials } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'
import { formatFactorValue } from '../utils/factorFormat.js'
import * as XLSX from 'xlsx'

const EXPORT_HEADERS = [
  '排名', '代码', '名称', '所属行业', '题材', '基期价', '最新价', '涨跌额', '跌幅(%)',
  '市值(亿)', '家数', '持股变化', '景气度-成长', '景气度-加速', '景气度-现金流',
  '跌幅百分位(%)', '市值百分位(%)', '家数百分位(%)', '综合得分',
]

// 景气度标签元信息：key 对应后端 prosperity 字段，fk 对应 factorFormat 的量纲键
const PROSPERITY_META = [
  { key: 'npYoy', short: '成长', label: '净利同比', fk: 'np_yoy' },
  { key: 'npAccel', short: '加速', label: '业绩加速度', fk: 'np_accel' },
  { key: 'cfq', short: '现金流', label: '现金流含量', fk: 'cfq' },
]
const PROSPERITY_KEYS = PROSPERITY_META.map((m) => m.key)
const TAG_CN = { positive: '正向', negative: '负向', neutral: '中性' }

export default {
  name: 'SystemRecommend',
  data() {
    return {
      items: [],
      summary: null,
      selectedRows: [],
      loading: false,
      queried: false,
      error: '',
      tableHeight: 560,
      prosperityFilter: 'all',
      // 题材筛选：只在当前会话生效（不落库、不记忆），刷新即恢复
      themeFilterMode: 'exclude',
      themeFilterThemes: [],
      themeOptions: [],
      prosperityMeta: PROSPERITY_META,
    }
  },
  computed: {
    themeOptionGroups() {
      const groups = []
      const index = new Map()
      for (const o of this.themeOptions) {
        const cat = o.category || '未分组'
        if (!index.has(cat)) {
          index.set(cat, groups.length)
          groups.push({ category: cat, options: [] })
        }
        groups[index.get(cat)].options.push(o)
      }
      return groups
    },
    themeFilterPlaceholder() {
      return this.themeFilterMode === 'exclude' ? '排除题材（可多选）' : '只看题材（可多选）'
    },
    filterActive() {
      return this.prosperityFilter !== 'all' || this.themeFilterThemes.length > 0
    },
    filteredItems() {
      let list = this.items
      if (this.prosperityFilter !== 'all' && list.length) {
        if (this.prosperityFilter === 'exclude-negative') {
          // 负面清单：任一指标负向即剔除（无标签的保留，不误伤）
          list = list.filter((it) => {
            const p = it.prosperity
            if (!p) return true
            return !PROSPERITY_KEYS.some((k) => p[k] && p[k].tag === 'negative')
          })
        } else {
          // positive-only：三项全部正向
          list = list.filter((it) => {
            const p = it.prosperity
            return p && PROSPERITY_KEYS.every((k) => p[k] && p[k].tag === 'positive')
          })
        }
      }
      if (this.themeFilterThemes.length && list.length) {
        const set = new Set(this.themeFilterThemes)
        list = list.filter((it) => {
          const hit = (it.themes || []).some((t) => set.has(t.themeId))
          return this.themeFilterMode === 'exclude' ? !hit : hit
        })
      }
      return list
    },
  },
  watch: {
    items() {
      this.updateTableHeight()
    },
    filteredItems() {
      this.updateTableHeight()
    },
    summary() {
      this.updateTableHeight()
    },
    error() {
      this.updateTableHeight()
    },
  },
  async created() {
    await this.loadData()
  },
  mounted() {
    this.updateTableHeight()
    window.addEventListener('resize', this.updateTableHeight, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,
    updateTableHeight() {
      this.$nextTick(() => {
        const shell = this.$refs.tableShell
        if (!shell) return
        const top = shell.getBoundingClientRect().top
        const height = Math.max(280, Math.floor(window.innerHeight - top - 20))
        if (height !== this.tableHeight) {
          this.tableHeight = height
        }
        this.$nextTick(() => {
          const table = this.$refs.recTable
          if (table?.doLayout) table.doLayout()
        })
      })
    },
    async loadData() {
      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const { res, data } = await fetchWithCredentials('/api/system-recommend')
        if (!res.ok) {
          throw new Error(data.error || `推荐接口失败 ${res.status}`)
        }
        this.summary = {
          quarter: data.quarter,
          asOf: data.asOf,
          latestDate: data.latestDate,
          poolSize: data.poolSize,
          availableSize: data.availableSize,
          count: data.count,
          thresholds: data.thresholds,
          prosperityPeriod: data.prosperityPeriod || '',
          themeTaggedStocks: data.themeTaggedStocks || 0,
        }
        this.items = data.items || []
        this.themeOptions = data.themeOptions || []
        // 选项变化后清掉失效的已选题材，避免筛选静默失效
        if (this.themeFilterThemes.length) {
          const valid = new Set(this.themeOptions.map((o) => o.id))
          this.themeFilterThemes = this.themeFilterThemes.filter((id) => valid.has(id))
        }
        this.selectedRows = []
        this.$nextTick(() => {
          const table = this.$refs.recTable
          if (table && table.clearSelection) table.clearSelection()
        })
      } catch (e) {
        this.items = []
        this.summary = null
        this.themeOptions = []
        this.error = e.message || String(e)
      } finally {
        this.loading = false
      }
    },
    onSelectionChange(rows) {
      this.selectedRows = rows || []
    },
    handleExportCommand(command) {
      if (command === 'all') {
        if (!this.filteredItems.length) {
          this.$message.warning('当前没有可导出的数据')
          return
        }
        this.exportRows(this.filteredItems, '全部')
      } else if (command === 'selected') {
        if (!this.selectedRows.length) {
          this.$message.warning('请先在表格中勾选要导出的行')
          return
        }
        const sorted = this.filteredItems
          .filter(item => this.selectedRows.includes(item))
          .sort((a, b) => a.rank - b.rank)
        this.exportRows(sorted, `已选${sorted.length}只`)
      }
    },
    prospTagOf(row, key) {
      return (row.prosperity && row.prosperity[key]) || null
    },
    prospTagClass(row, key) {
      const t = this.prospTagOf(row, key)
      return ['prosp-tag', `prosp-tag--${t ? t.tag : 'none'}`]
    },
    prospTooltip(row, key) {
      const meta = PROSPERITY_META.find((m) => m.key === key)
      const t = this.prospTagOf(row, key)
      if (!t) return `${meta.label}：无数据`
      return `${meta.label} ${formatFactorValue(meta.fk, t.value)}（全市场分位 ${t.pct}，${TAG_CN[t.tag] || t.tag}）`
    },
    prospExportValue(row, key) {
      const t = this.prospTagOf(row, key)
      return t ? TAG_CN[t.tag] || t.tag : ''
    },
    themeTooltip(row) {
      return (row.themes || [])
        .map((t) => (t.category ? `${t.category}·${t.themeName}` : t.themeName))
        .join('、')
    },
    themeExportValue(row) {
      return (row.themes || []).map((t) => t.themeName).join('、')
    },
    /** 点表格里的题材标签：直接加入排除列表（再点一次取消） */
    toggleExcludeTheme(themeId) {
      this.themeFilterMode = 'exclude'
      if (this.themeFilterThemes.includes(themeId)) {
        this.themeFilterThemes = this.themeFilterThemes.filter((id) => id !== themeId)
      } else {
        this.themeFilterThemes = this.themeFilterThemes.concat(themeId)
      }
    },
    exportRows(rows, label) {
      const data = rows.map(row => ({
        '排名': row.rank,
        '代码': row.code,
        '名称': row.name,
        '所属行业': row.industry || '',
        '题材': this.themeExportValue(row),
        '基期价': this.round2(row.priceAsOf),
        '最新价': this.round2(row.priceLatest),
        '涨跌额': this.round2(row.change),
        '跌幅(%)': this.round2(row.declinePct),
        '市值(亿)': this.round2(row.marketValue),
        '家数': row.shares,
        '持股变化': row.action || '',
        '景气度-成长': this.prospExportValue(row, 'npYoy'),
        '景气度-加速': this.prospExportValue(row, 'npAccel'),
        '景气度-现金流': this.prospExportValue(row, 'cfq'),
        '跌幅百分位(%)': this.round1(this.percentNum(row.pd)),
        '市值百分位(%)': this.round1(this.percentNum(row.pm)),
        '家数百分位(%)': this.round1(this.percentNum(row.ps)),
        '综合得分': this.round2(row.score),
      }))
      const sheet = XLSX.utils.json_to_sheet(data, { header: EXPORT_HEADERS })
      sheet['!cols'] = EXPORT_HEADERS.map((h) => ({ wch: h.length > 4 ? 12 : 9 }))
      const book = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(book, sheet, '系统推荐')
      const asOf = this.summary ? this.summary.asOf : ''
      const latest = this.summary ? this.summary.latestDate : ''
      XLSX.writeFile(book, `系统推荐_${asOf}_${latest}_${label}.xlsx`)
      this.$message.success(`已导出 ${rows.length} 只（${label}）`)
    },
    round2(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      return Number.isFinite(n) ? Number(n.toFixed(2)) : ''
    },
    round1(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      return Number.isFinite(n) ? Number(n.toFixed(1)) : ''
    },
    percentNum(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      return Number.isFinite(n) ? n * 100 : ''
    },
    formatPrice(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : ''
    },
    formatPct(v) {
      if (v == null || v === '') return ''
      return Number(v).toFixed(2)
    },
    formatScore(v) {
      if (v == null || v === '') return ''
      return Number(v).toFixed(2)
    },
    formatPercentile(v) {
      if (v == null || v === '') return ''
      return (Number(v) * 100).toFixed(1) + '%'
    },
    actionTagType(action) {
      if (!action) return 'info'
      if (action.includes('增')) return 'danger'
      if (action.includes('减')) return 'success'
      if (action.includes('新进')) return 'warning'
      return 'info'
    },
  },
}
</script>

<style scoped>
.system-recommend {
  width: 100%;
  min-width: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0 0 12px;
  text-align: left;
}

/* 题材筛选：模式 + 题材多选，两段紧贴成一组 */
.theme-filter {
  display: inline-flex;
  align-items: center;
}

.theme-filter__mode {
  width: 110px;
}

.theme-filter__value {
  width: 248px;
}

.toolbar-tip {
  font-size: 13px;
  color: #909399;
}

.summary-bar {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 14px 16px;
  margin: 0 0 12px;
  text-align: left;
}

.summary-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 10px;
}

.summary-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 92px;
}

.summary-label {
  font-size: 12px;
  color: #909399;
}

.summary-value {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.summary-cell--accent .summary-value {
  color: #f56c6c;
  font-size: 18px;
}

/* 筛选生效时附在数量后的「/ 共 N」，别抢主数值视觉 */
.summary-delta {
  margin-left: 2px;
  font-size: 12px;
  font-weight: 400;
  font-style: normal;
  color: #909399;
}

.threshold-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  color: #606266;
}

.threshold-label {
  margin-right: 4px;
  font-weight: 500;
}

.empty-tip {
  margin: 0 0 12px;
  text-align: left;
  font-size: 13px;
  color: #909399;
  padding: 16px;
}

.up {
  color: #f56c6c;
}

.down {
  color: #67c23a;
}

.score-cell {
  font-weight: 600;
  color: #303133;
}

.prosp-tags {
  display: flex;
  gap: 3px;
  justify-content: center;
  align-items: center;
}

.prosp-tag {
  display: inline-block;
  font-size: 11px;
  line-height: 18px;
  padding: 0 5px;
  border-radius: 3px;
  border: 1px solid #e9e9eb;
  background: #fafafa;
  color: #c0c4cc;
  cursor: default;
  white-space: nowrap;
}

.prosp-tag--positive {
  color: #f56c6c;
  border-color: #fbc4c4;
  background: #fef0f0;
}

.prosp-tag--negative {
  color: #67c23a;
  border-color: #b3e19d;
  background: #f0f9eb;
}

.prosp-tag--neutral {
  color: #909399;
  border-color: #d3d4d6;
  background: #f4f4f5;
}

.prosp-none {
  color: #c0c4cc;
  font-size: 12px;
}

/* 题材列：小圆角 chip，点一下即按该题材排除 */
.theme-tags {
  display: flex;
  align-items: center;
  gap: 3px;
  overflow: hidden;
}

.theme-chip {
  display: inline-block;
  max-width: 68px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 18px;
  padding: 0 5px;
  border-radius: 3px;
  color: #4f63b0;
  border: 1px solid #c9d5f2;
  background: #f2f5ff;
  cursor: pointer;
  transition: background 0.15s;
}

.theme-chip:hover {
  background: #e3eaff;
}

.theme-chip--hit {
  color: #b8850f;
  border-color: #f0d9a8;
  background: #fdf6ec;
}

.theme-chip--more {
  max-width: none;
  cursor: default;
  color: #909399;
  border-color: #dcdfe6;
  background: #f4f4f5;
}

.theme-none {
  color: #c0c4cc;
  font-size: 12px;
}

.table-shell {
  min-height: 0;
}
</style>